<template>
  <li
    class="pb-preset-item"
    :class="{
      active: props.active,
      'pb-preset-item-any-is-dragged': anyPresetIsDragged,
      'pb-preset-item-current-is-dragged': currentPresetIsDragged,
      'pb-preset-item-readonly': readonly,
    }"
  >
    <!-- Preset DMX Range -->
    <div class="pb-preset-item-range">
      <BaseInput
        :id="`preset-${preset.data._uid}-range-start`"
        :type="INPUT_TYPE_NUMBER"
        class="pb-preset-item-range-content pb-preset-item-range-start"
        :class="{
          'pb-preset-item-range-editable': !startRangeInputDisabled,
          'pb-preset-item-range-readonly': startRangeInputDisabled,
        }"
        :readonly="startRangeInputDisabled"
        :value="start"
        :autoselect="!startRangeInputDisabled"
        theme="reset"
        :model-modifiers="{ lazy: true }"
        @input="rangeStartChanged"
      />
      <BaseInput
        :id="`preset-${preset.data._uid}-range-end`"
        :type="INPUT_TYPE_NUMBER"
        class="pb-preset-item-range-content pb-preset-item-range-end"
        :class="{
          'pb-preset-item-range-editable': !endRangeInputDisabled,
          'pb-preset-item-range-readonly': endRangeInputDisabled,
        }"
        :readonly="endRangeInputDisabled"
        :value="end"
        :autoselect="!endRangeInputDisabled"
        theme="reset"
        :model-modifiers="{ lazy: true }"
        @input="rangeEndChanged"
      />
    </div>
    <!-- End Preset DMX Range -->
    <!-- Preset Block -->
    <label
      :for="`preset-${preset.data._uid}-range`"
      class="pb-preset-item-content"
    >
      <!-- Dropzone -->
      <div
        class="pb-preset-item-dropzone-wrapper"
      >
        <div
          ref="topDropzone"
          class="pb-preset-item-dropzone pb-preset-item-dropzone-top"
          :class="{ 'pb-preset-item-dropzone-active': topDropzoneActive }"
        />
        <div
          ref="bottomDropzone"
          class="pb-preset-item-dropzone pb-preset-item-dropzone-bottom"
          :class="{ 'pb-preset-item-dropzone-active': bottomDropzoneActive }"
        />
      </div>
      <!-- End Dropzone -->
      <!-- Actual Preset -->
      <Linkbox
        class="pb-preset-item-data"
        :to="href"
        @click.prevent="emit('navigate')"
      >
        <template #before>
          <div
            ref="reorderGrabber"
            class="pb-preset-item-reorder"
            :class="{ 'pb-preset-item-reorder-draggable': dragEnabled }"
          >
            <i class="icon-move-bar" />
          </div>
          <PbIcon
            class="pb-preset-item-icon"
            :value="icon"
          />
        </template>

        <div
          class="pb-preset-item-text-wrapper"
          :style="presetSizingStyle"
        >
          <div class="pb-preset-item-text">
            <div class="pb-preset-item-name">
              {{ preset.linkName }}
            </div>
            <PbItemDetails v-if="flags">
              {{ flags }}
            </PbItemDetails>
          </div>
          <div class="pb-preset-item-icons-wrapper">
            <ReportIcon
              class="pb-preset-item-report"
              :report="preset.report"
            />
            <LinkIcon
              v-bind="preset.data"
            />
          </div>
        </div>
      </Linkbox>
      <!-- End Actual Preset -->
      <!-- Resizers -->
      <div
        v-if="preset.data.dmx_start !== 0 && !readonly"
        ref="topResizer"
        class="pb-preset-item-resizer pb-preset-item-resizer-top"
        :class="{
          'pb-preset-item-resizer-allow-top': startCanGrow,
          'pb-preset-item-resizer-allow-bottom': startCanShrink,
          'pb-preset-item-resizer-disabled': !startCanShrink,
          'pb-preset-item-resizer-active': resizingTop,
        }"
      >
        <div class="pb-preset-item-resizer-icons">
          <i class="icon-arrowdown" />
        </div>
      </div>
      <div
        v-if="preset.data.dmx_end !== 255 && !readonly"
        ref="bottomResizer"
        class="pb-preset-item-resizer pb-preset-item-resizer-bottom"
        :class="{
          'pb-preset-item-resizer-allow-top': endCanShrink,
          'pb-preset-item-resizer-allow-bottom': endCanGrow,
          'pb-preset-item-resizer-disabled': !endCanShrink,
          'pb-preset-item-resizer-active': resizingBottom,
        }"
      >
        <div class="pb-preset-item-resizer-icons">
          <i class="icon-arrowup" />
        </div>
      </div>
      <!-- End Resizers -->
    </label>
    <!-- End Preset Block -->
    <!-- Preset Dragging Follower -->
    <PbItem
      v-if="currentPresetIsDragged"
      class="pb-preset-item-drag-follower"
      :icon="icon"
      :style="dragFollowerStyle"
    >
      {{ preset.linkName }}
      <PbItemDetails v-if="flags">
        {{ flags }}
      </PbItemDetails>
    </PbItem>
    <!-- End Preset Dragging Follower -->
  </li>
</template>

<script setup>
import {
  computed,
  ref,
  toRef,
  watch,
} from 'vue'

import BaseInput from '@nicolaudie-group/ng-ui-kit/components/BaseInput/BaseInput.vue'
import capitalize from '@nicolaudie-group/ng-ui-kit/helpers/capitalize'
import useTouch from '@nicolaudie-group/ng-ui-kit/composables/useTouch'

import { presetHelpers } from '@/helpers/profile'
import useRootStore from '@/stores/root.store'

import PbIcon from '@/components/PbIcon/PbIcon.vue'
import Linkbox from '@/components/LinkBox/LinkBox.vue'
import ReportIcon from '@/components/ItemIcons/ReportIcon.vue'
import PbItem from '@/components/PbItem/PbItem.vue'
import LinkIcon from '@/components/ItemIcons/LinkIcon.vue'

import { useDrag, useDrop } from '@/composables/useDragDrop'
import { useReadonly } from '@/composables/useProfile'
import useTranslate from '@/composables/useTranslate'
import PbItemDetails from '@/components/PbItemDetails/PbItemDetails.vue'
import { INPUT_TYPE_NUMBER } from '@nicolaudie-group/ng-ui-kit/components/BaseInput/symbols'

const props = defineProps({
  preset: { type: Object, default: () => ({}) },
  start: { type: Number, default: 0 },
  end: { type: Number, default: 255 },
  size: { type: Number, default: 256 },
  startCanGrow: { type: Boolean, default: false },
  startCanShrink: { type: Boolean, default: false },
  startCanChange: { type: Boolean, default: false },
  endCanGrow: { type: Boolean, default: false },
  endCanShrink: { type: Boolean, default: false },
  endCanChange: { type: Boolean, default: false },
  dragDropContext: { type: Object, default: () => ({}) },
  loading: { type: Boolean, default: false },
  href: { type: String, default: '' },
  active: { type: Boolean, default: false },
})

const emit = defineEmits([
  'navigate',
  'submit',
  'update-start',
  'update-end',
])

const { translate } = useTranslate()

const rootStore = useRootStore()

const RATIO = 1

const bottomResizer = ref(null)
const topResizer = ref(null)
const reorderGrabber = ref(null)
const topDropzone = ref(null)
const bottomDropzone = ref(null)

const preset = toRef(props, 'preset')

const readonly = useReadonly()

const icon = computed(() => presetHelpers.getIcon(preset.value.data))

const flags = computed(() => {
  const result = []
  if (preset.value.data.is_default) {
    result.push(translate('pb2.presetFlags.default'))
  }
  if (preset.value.data.show_dimmer) {
    result.push(translate('pb2.presetFlags.linear'))
  }
  if (preset.value.data.random) {
    result.push(translate('pb2.presetFlags.random'))
  }
  if (preset.value.data.prority) {
    result.push(translate('pb2.presetFlags.priority'))
  }
  if (preset.value.data.effect_type) {
    result.push(translate('pb2.presetFlags.reversed'))
  }
  if (preset.value.data.shake) {
    result.push(translate('pb2.presetFlags.shake'))
  }
  if (preset.value.data.index) {
    result.push(translate('pb2.presetFlags.index'))
  }
  if (preset.value.data.bounce) {
    result.push(translate('pb2.presetFlags.bounce'))
  }
  if (preset.value.data.rotary) {
    result.push(translate('pb2.presetFlags.rotary'))
  }
  if (preset.value.data.self_rotary) {
    result.push(translate('pb2.presetFlags.selfRotary'))
  }

  return capitalize(result.join(', '))
})

const startOffset = ref(0)
const endOffset = ref(0)
const enable = computed(() => !props.loading)

const startRangeInputDisabled = computed(() => (
  !props.startCanChange
  || props.loading
  || readonly.value
))
const endRangeInputDisabled = computed(() => (
  !props.endCanChange
  || props.loading
  || readonly.value
))

const { isTouching: resizingTop } = useTouch(
  topResizer,
  {
    horizontal: false,
    vertical: true,
    initialY: startOffset,
    enable,
    relative: true,
  },
)

const { isTouching: resizingBottom } = useTouch(
  bottomResizer,
  {
    horizontal: false,
    vertical: true,
    initialY: endOffset,
    enable,
    relative: true,
  },
)

watch(resizingTop, () => {
  if (!resizingTop.value) {
    // when the top resizer is released
    // submit the range start/range end
    emit('submit')
    startOffset.value = props.start * RATIO
  }
})

watch(resizingBottom, () => {
  if (!resizingBottom.value) {
    // when the bottom resizer is released
    // submit the range start/range end
    emit('submit')
    endOffset.value = props.end * RATIO
  }
})

watch(props, () => {
  startOffset.value = props.start * RATIO
  endOffset.value = props.end * RATIO
}, { immediate: true, deep: true })

watch(startOffset, () => {
  if (resizingTop.value) {
    // When the start offset changes, and we're resizing from the top
    emit('update-start', preset.value, Math.ceil(startOffset.value / RATIO))
  }
})

watch(endOffset, () => {
  if (resizingBottom.value) {
    // When the end offset changes, and we're resizing from the bottom
    emit('update-end', preset.value, Math.ceil(endOffset.value / RATIO))
  }
})

const presetSizingStyle = computed(() => (
  { padding: `${(props.size * RATIO) / 2}px 0` }
))

const parseNumberInput = (value) => (
  typeof value === 'number' ? value : parseInt(value, 10)
)

// Handle preset distribution
const rangeStartChanged = (value) => {
  emit('update-start', preset.value, parseNumberInput(value))
  emit('submit')
}

const rangeEndChanged = (value) => {
  emit('update-end', preset.value, parseNumberInput(value))
  emit('submit')
}

const anyPresetIsDragged = computed(() => props.dragDropContext.isDragging.value)

const dragEnabled = computed(() => !readonly.value)

const { isDragging: currentPresetIsDragged, dragX, dragY } = useDrag(
  reorderGrabber,
  props.dragDropContext,
  preset,
  dragEnabled,
)

watch(currentPresetIsDragged, () => {
  // Add the "grabbed" cursor on everything if we're dragging
  rootStore.rootClasses['pb-dragging-active'] = currentPresetIsDragged
})

const dragFollowerStyle = computed(() => (
  { transform: `translateX(${dragX.value}px) translateY(-50%) translateY(${dragY.value}px)` }
))

const { active: topDropzoneActive } = useDrop(
  topDropzone,
  props.dragDropContext,
  {
    before: true,
    preset,
  },
)

const { active: bottomDropzoneActive } = useDrop(
  bottomDropzone,
  props.dragDropContext,
  {
    after: true,
    preset,
  },
)
</script>

<style lang="scss" src="./PresetItem.scss" />
